import React, { useId } from 'react';
import { formatMoney } from '../../../utils/currency';
import { Button } from '../../BasicComponents/Button';
import { Input } from '../../BasicComponents/Input';
import type { LoyaltyRedemptionInputProps } from './types';

// ─── LoyaltyRedemptionInput ───────────────────────────────────────────────────

export function LoyaltyRedemptionInput({
  availablePoints,
  pointValue,
  currency = 'INR',
  maxRedeemable,
  value,
  onChange,
  minimumPoints = 0,
  disabled = false,
  className,
}: LoyaltyRedemptionInputProps) {
  const inputId = useId();
  const sliderId = useId();
  const liveId = useId();

  const effectiveMax = Math.min(availablePoints, maxRedeemable);
  // `pointValue` is already minor units, so points × value IS the amount. The
  // extra /100 here made every redemption read a hundredth of its real worth —
  // and the formatter below divides by the subunit again, so 10 points at
  // ₹1/point announced "₹0.10 off". Server-side truth: customers.repository.ts
  // computes `points * pointValuePaise` with no divisor.
  const redemptionAmount = value * pointValue;

  const isAtMax = value >= effectiveMax;
  const isBelowMinimum = value > 0 && minimumPoints > 0 && value < minimumPoints;
  const isApplied = value > 0 && !isBelowMinimum;

  function handleSliderChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(parseInt(e.target.value, 10));
  }

  function handleInputChange(value: string) {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed)) {
      onChange(0);
    } else {
      onChange(Math.min(effectiveMax, Math.max(0, parsed)));
    }
  }

  function handleUseAll() {
    onChange(effectiveMax);
  }

  function handleClear() {
    onChange(0);
  }

  if (disabled) {
    return (
      <div
        className={[
          'rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-400',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        Link customer to redeem points
      </div>
    );
  }

  return (
    <div
      className={[
        'rounded-lg border p-3 space-y-3 transition-colors',
        isApplied ? 'border-success-300 bg-success-50' : 'border-neutral-200 bg-white',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Header row */}
      <div className="flex items-center justify-between">
        <label htmlFor={sliderId} className="text-sm font-medium text-neutral-700">
          Redeem Points
        </label>
        <span className="text-xs text-neutral-500">
          {availablePoints.toLocaleString('en-IN')} available
        </span>
      </div>

      {/* Slider */}
      <input
        id={sliderId}
        type="range"
        min={0}
        max={effectiveMax}
        step={1}
        value={value}
        onChange={handleSliderChange}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={effectiveMax}
        aria-label={`Loyalty points to redeem: ${value}`}
        className="w-full accent-brand-600"
      />

      {/* Number input + use-all / clear */}
      <div className="flex items-center gap-2">
        <Input
          id={inputId}
          type="number"
          value={value === 0 ? '' : String(value)}
          onChange={handleInputChange}
          placeholder="0"
          min={0}
          max={effectiveMax}
          size="sm"
          className="w-24 tabular-nums"
          aria-label="Points to redeem"
        />
        <span className="text-xs text-neutral-500 flex-1">pts</span>
        {value > 0 ? (
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-danger-600 hover:underline"
          >
            Clear
          </button>
        ) : (
          <button
            type="button"
            onClick={handleUseAll}
            disabled={effectiveMax === 0}
            className="text-xs text-brand-600 hover:underline disabled:opacity-40"
          >
            Use all
          </button>
        )}
      </div>

      {/* Rupee equivalent live region */}
      <div
        id={liveId}
        aria-live="polite"
        aria-atomic="true"
        className={[
          'text-sm font-medium tabular-nums',
          isApplied ? 'text-success-700' : 'text-neutral-600',
        ].join(' ')}
      >
        {value > 0
          ? `${value.toLocaleString('en-IN')} pts = ${formatMoney(redemptionAmount, currency)} off`
          : 'Enter points to see equivalent value'}
      </div>

      {/* Validation messages */}
      {isAtMax && value > 0 && (
        <p className="text-xs text-warning-700">Maximum redemption for your role</p>
      )}
      {isBelowMinimum && (
        <p className="text-xs text-info-700">
          Minimum {minimumPoints.toLocaleString('en-IN')} points required
        </p>
      )}

      {/* Apply button */}
      {value > 0 && !isBelowMinimum && (
        <Button
          variant="primary"
          size="sm"
          className="w-full"
          aria-label={`Apply ${value} points redemption`}
        >
          {isApplied ? `Applied — ${formatMoney(redemptionAmount, currency)} off` : 'Apply'}
        </Button>
      )}
    </div>
  );
}
